"use client";

import { IconFileText } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconAlertInfoOutline18, IconCheckDoubleOutline18, IconCheckOutline18, IconChevronExpandYOutline18, IconCode2Outline18, IconCopyOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { V2_ENDPOINT } from "@/consts";
import { useContractProjects } from "@/hooks/useContractProject";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { getChainInfo } from "@/lib/chains";
import { cn, formatAddress } from "@/lib/utils";
import type { ContractProject, DeployResult } from "@/types/contract";
import { getChainConfig, getDeployAddress } from "@/types/contract";
import { PixelLoader } from "@/components/ui/pixel-loader";
import * as React from "react";

// Special value for custom bytecode mode
const CUSTOM_MODE_VALUE = "__custom__";

/** A flattened entry representing a specific deployment of a contract project */
interface DeploymentEntry {
  project: ContractProject;
  chainId: number;
  deployment: DeployResult;
}
interface ContractProjectSelectorProps {
  label: string;
  description?: string;
  /** The contract project ID (or empty/undefined for custom mode) */
  value: string | undefined;
  onChange: (value: string) => void;
  /** Callback to also update contractSource field */
  onContractSourceChange?: (source: "custom" | "project") => void;
  /** Callback to store project ABI for constructor args detection */
  onProjectAbiChange?: (abi: string) => void;
  /** Current contractSource value to determine selection state */
  contractSource?: string;
  /** Callback when a project is selected, provides full project data */
  onProjectSelect?: (project: ContractProject | null) => void;
  /** Filter to only show projects with bytecode (ready to deploy) */
  requireBytecode?: boolean;
  /** Callback to set chain ID when a deployment is selected */
  onChainIdChange?: (chainId: number) => void;
  /** Currently selected chain ID (for highlighting selected deployment) */
  selectedChainId?: number;
  /** Callback to set contract address when a deployment is selected */
  onContractAddressChange?: (address: string) => void;
}

/**
 * Contract Project Selector
 *
 * A unified dropdown that allows selecting either:
 * - Custom mode - for manual input (bytecode/ABI for deploy, address/ABI for read/write)
 * - A contract project - fetches data from the API
 *
 * When requireBytecode=false (evm-read/evm-write mode), shows individual deployments
 * for each project so users can select a specific chain deployment.
 */
function ContractProjectSelector({
  label,
  description,
  value,
  onChange,
  onContractSourceChange,
  onProjectAbiChange,
  contractSource,
  onProjectSelect,
  requireBytecode = true,
  onChainIdChange,
  selectedChainId,
  onContractAddressChange
}: ContractProjectSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [copiedAddress, setCopiedAddress] = React.useState<string | null>(null);
  const fetchRequestCounterRef = React.useRef(0);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clean up copy timer on unmount
  React.useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  // Memoize options to prevent infinite loop in useContractProjects
  const fetchOptions = React.useMemo(() => ({
    limit: 100,
    sort: "updatedAt" as const,
    order: "desc" as const
  }), []);

  // Fetch contract projects
  const {
    projects,
    isLoading,
    error
  } = useContractProjects(fetchOptions);

  // Statuses that indicate a project has been compiled and has bytecode
  const COMPILED_STATUSES = ["testing", "tested", "auditing", "audited", "deploying", "testnet_deployed", "mainnet_deployed"];

  // Filter projects based on requirements and search
  const filteredProjects = React.useMemo(() => {
    let filtered = projects;

    // Filter to only projects that are compiled (have passed writing phase)
    if (requireBytecode) {
      filtered = filtered.filter(p => p.source?.bytecode || COMPILED_STATUSES.includes(p.status));
    }

    // Filter by search query
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      filtered = filtered.filter(p_0 => p_0.name.toLowerCase().includes(lowerQuery) || p_0.source?.contractName?.toLowerCase().includes(lowerQuery));
    }
    return filtered;
  }, [projects, requireBytecode, searchQuery]);

  // Build flattened deployment entries for evm-read/evm-write mode
  const deploymentEntries = React.useMemo(() => {
    if (requireBytecode) return [];
    const entries: DeploymentEntry[] = [];
    for (const project of filteredProjects) {
      if (project.deployments) {
        for (const [chainIdStr, deployment] of Object.entries(project.deployments)) {
          const address = getDeployAddress(deployment);
          if (address) {
            entries.push({
              project,
              chainId: parseInt(chainIdStr, 10),
              deployment
            });
          }
        }
      }
    }
    return entries;
  }, [filteredProjects, requireBytecode]);

  // Determine if we're in custom mode
  const isCustomMode = contractSource === "custom" || !value && !contractSource;

  // Find the currently selected project
  const selectedProject = React.useMemo(() => {
    if (!value || isCustomMode) return null;
    return projects.find(p_1 => p_1.id === value) || null;
  }, [value, projects, isCustomMode]);
  const [isFetchingProject, setIsFetchingProject] = React.useState(false);

  /** Normalize ABI to a JSON string (handles both string and array formats from API) */
  const normalizeAbi = (abi: unknown): string | null => {
    if (!abi) return null;
    // Backend stores ABI as a JSON string, but TS type says unknown[]
    // Handle both cases to avoid double-encoding
    if (typeof abi === "string") {
      // Already a JSON string - validate it's non-empty and valid JSON
      if (abi === "" || abi === "[]" || abi === "null") return null;
      try {
        const parsed = JSON.parse(abi);
        if (Array.isArray(parsed) && parsed.length > 0) return abi;
        return null;
      } catch {
        return null; // Invalid JSON string
      }
    }
    if (Array.isArray(abi) && abi.length > 0) {
      return JSON.stringify(abi);
    }
    return null;
  };

  /** Fetch full project details and set ABI (with staleness guard) */
  const fetchAndSetProjectAbi = async (project_0: ContractProject) => {
    // If ABI is available in list data, use it immediately
    const listAbi = normalizeAbi(project_0.source?.abi);
    if (listAbi) {
      onProjectAbiChange?.(listAbi);
      onProjectSelect?.(project_0);
      return;
    }

    // Track request to guard against stale responses from rapid selection
    const requestId = ++fetchRequestCounterRef.current;

    // Otherwise fetch full project details to get the ABI
    setIsFetchingProject(true);
    try {
      const response = await authenticatedFetch(`${V2_ENDPOINT}/v1/contracts/${project_0.id}`);
      if (requestId !== fetchRequestCounterRef.current) return; // Stale request
      if (response.ok) {
        const data = await response.json();
        const fullProject = data.data || data;
        const fetchedAbi = normalizeAbi(fullProject.source?.abi);
        if (fetchedAbi) {
          onProjectAbiChange?.(fetchedAbi);
        }
        onProjectSelect?.(fullProject);
      } else {
        onProjectSelect?.(project_0);
      }
    } catch {
      if (requestId !== fetchRequestCounterRef.current) return; // Stale request
      onProjectSelect?.(project_0);
    } finally {
      if (requestId === fetchRequestCounterRef.current) {
        setIsFetchingProject(false);
      }
    }
  };
  const handleSelectProject = async (project_1: ContractProject) => {
    onChange(project_1.id);
    onContractSourceChange?.("project");
    setOpen(false);
    await fetchAndSetProjectAbi(project_1);
  };

  /** Handle selecting a specific deployment (evm-read/evm-write mode) */
  const handleSelectDeployment = async (entry: DeploymentEntry) => {
    onChange(entry.project.id);
    onContractSourceChange?.("project");
    onChainIdChange?.(entry.chainId);
    const address_0 = getDeployAddress(entry.deployment);
    if (address_0) {
      onContractAddressChange?.(address_0);
    }
    setOpen(false);
    await fetchAndSetProjectAbi(entry.project);
  };
  const handleSelectCustom = () => {
    onChange(""); // Clear project ID
    onContractSourceChange?.("custom");
    onProjectAbiChange?.(""); // Clear ABI
    onProjectSelect?.(null);
    setOpen(false);
  };

  // Get status badge color
  const getStatusColor = (status: ContractProject["status"]) => {
    switch (status) {
      case "testnet_deployed":
      case "mainnet_deployed":
        return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400";
      case "testing":
      case "tested":
      case "auditing":
      case "audited":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "deploying":
        return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400";
      case "failed":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400";
      default:
        return "bg-ds-neutral-100 text-ds-text-secondary dark:bg-ds-neutral-800 dark:text-ds-text-disabled";
    }
  };

  /** Get display name for a chain ID */
  const getChainName = (chainId: number): string => {
    // Try deployment chains first (includes testnets)
    const chainConfig = getChainConfig(chainId);
    if (chainConfig) return chainConfig.name;
    // Fall back to general chain info
    return getChainInfo(chainId).name;
  };
  const customLabel = requireBytecode ? "Custom bytecode" : "Custom contract address";
  const customDescription = requireBytecode ? "Enter bytecode and ABI manually" : "Enter contract address and ABI manually";

  // Render the selected value display
  const renderSelectedValue = () => {
    if (isFetchingProject) {
      return <span className="flex items-center gap-2">
          <PixelLoader size="sm" color="blue" />
          <span className="text-ds-text-muted">Loading project...</span>
        </span>;
    }
    if (isCustomMode) {
      return <span className="flex items-center gap-2">
          <IconCode2Outline18 className="h-4 w-4 text-blue-500" />
          <span className="text-ds-text-primary">{customLabel}</span>
        </span>;
    }
    if (selectedProject) {
      // In deployment mode, show chain info too
      if (!requireBytecode && selectedChainId) {
        const chainName = getChainName(selectedChainId);
        return <span className="flex items-center gap-2">
            <IconFileText className="h-4 w-4 text-blue-500" />
            <span className="text-ds-text-primary">{selectedProject.name}</span>
            <span className="label-xs">· {chainName}</span>
          </span>;
      }
      return <span className="flex items-center gap-2">
          <IconFileText className="h-4 w-4 text-blue-500" />
          <span className="text-ds-text-primary">{selectedProject.name}</span>
          {selectedProject.source?.contractName && <span className="label-xs">({selectedProject.source.contractName})</span>}
          <span className={cn("text-3xs ml-auto rounded-full px-2 py-0.5 font-medium", getStatusColor(selectedProject.status))}>
            {selectedProject.status}
          </span>
        </span>;
    }
    return <span className="text-ds-text-disabled dark:text-ds-text-muted">Select contract source...</span>;
  };

  /** Render deployment entries (evm-read/evm-write mode) */
  const renderDeploymentEntries = () => {
    if (deploymentEntries.length === 0) {
      return <div className="body-sm py-4 text-center">No deployed contracts found.</div>;
    }
    return deploymentEntries.map(entry_0 => {
      const address_1 = getDeployAddress(entry_0.deployment) || "";
      const chainName_0 = getChainName(entry_0.chainId);
      const isSelected = value === entry_0.project.id && !isCustomMode && selectedChainId === entry_0.chainId;
      const entryKey = `${entry_0.project.id}-${entry_0.chainId}`;
      return <CommandItem key={entryKey} value={entryKey} onSelect={() => handleSelectDeployment(entry_0)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800">
          <div className="flex w-full cursor-pointer items-center gap-2">
            <IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
            <IconFileText className="h-4 w-4 flex-shrink-0 text-blue-500" />
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-center gap-1.5">
                <span className="truncate font-medium">{entry_0.project.name}</span>
                <span className="caption-xs">·</span>
                <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{chainName_0}</span>
              </div>
              <span className="label-xs truncate">{formatAddress(address_1)}</span>
            </div>
            <button type="button" className="text-ds-text-disabled hover:bg-ds-neutral-200 hover:text-ds-text-subtle dark:hover:bg-ds-neutral-700 flex-shrink-0 rounded p-1 dark:hover:text-zinc-300" title="Copy contract address" onClick={e => {
            e.stopPropagation();
            e.preventDefault();
            navigator.clipboard.writeText(address_1).catch(() => {});
            setCopiedAddress(address_1);
            if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
            copyTimerRef.current = setTimeout(() => setCopiedAddress(null), 1500);
          }}>
              {copiedAddress === address_1 ? <IconCheckDoubleOutline18 className="h-3.5 w-3.5 text-green-500" /> : <IconCopyOutline18 className="h-3.5 w-3.5" />}
            </button>
            <span className={cn("text-3xs flex-shrink-0 rounded-full px-2 py-0.5 font-medium", entry_0.deployment.networkType === "mainnet" ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400")}>
              {entry_0.deployment.networkType || "testnet"}
            </span>
          </div>
        </CommandItem>;
    });
  };

  /** Render project entries (deploy-contract mode) */
  const renderProjectEntries = () => {
    if (filteredProjects.length === 0) {
      return <div className="body-sm py-4 text-center">No compiled contracts found. Build a contract first.</div>;
    }
    return filteredProjects.map(project_2 => <CommandItem key={project_2.id} value={project_2.id} onSelect={() => handleSelectProject(project_2)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800">
        <div className="flex w-full cursor-pointer items-center gap-2">
          <IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", value === project_2.id && !isCustomMode ? "opacity-100" : "opacity-0")} />
          <IconFileText className="h-4 w-4 flex-shrink-0 text-blue-500" />
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="truncate font-medium">{project_2.name}</span>
            <span className="caption-xs truncate font-mono">{project_2.id}</span>
          </div>
        </div>
      </CommandItem>);
  };
  return <div className="space-y-2">
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            {renderSelectedValue()}
            <IconChevronExpandYOutline18 className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-ds-surface-card !z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search projects..." value={searchQuery} onValueChange={setSearchQuery} />
            <CommandList>
              {error ? <div className="flex items-center gap-2 px-4 py-6 text-sm text-red-500">
                  <IconAlertInfoOutline18 className="h-4 w-4" />
                  <span>Failed to load projects. Please try again.</span>
                </div> : isLoading ? <div className="body-sm py-6 text-center">Loading projects...</div> : <>
                  {/* Custom mode option - always shown at top */}
                  <CommandGroup heading="Manual Input">
                    <CommandItem value={CUSTOM_MODE_VALUE} onSelect={handleSelectCustom} className="hover:bg-blue-50 dark:hover:bg-blue-950/20">
                      <div className="flex w-full cursor-pointer items-center gap-2">
                        <IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", isCustomMode ? "opacity-100" : "opacity-0")} />
                        <IconCode2Outline18 className="h-4 w-4 flex-shrink-0 text-blue-500" />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <span className="font-medium">{customLabel}</span>
                          <span className="label-xs">{customDescription}</span>
                        </div>
                      </div>
                    </CommandItem>
                  </CommandGroup>

                  {/* Contract projects / deployments */}
                  <CommandGroup heading={requireBytecode ? "Contract Projects" : "Deployed Contracts"}>
                    {requireBytecode ? renderProjectEntries() : renderDeploymentEntries()}
                  </CommandGroup>
                </>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>;
}
export default React.memo(ContractProjectSelector);